import React, { createContext, useCallback, useContext, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MessageCircle } from "lucide-react";
import { nextWhatsAppUrl } from "@/lib/whatsapp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LeadDialogContext = createContext({ open: () => {} });

export const useLeadDialog = () => useContext(LeadDialogContext);

const emptyForm = {
  nombre: "",
  rut: "",
  telefono: "",
  correo: "",
  monto: "",
};

export function LeadDialogProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [origen, setOrigen] = useState("landing");
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const reset = () => {
    setForm(emptyForm);
    setErrorMsg("");
    setSubmitting(false);
  };

  const open = useCallback((src = "landing") => {
    setOrigen(src);
    reset();
    setIsOpen(true);
  }, []);

  const change = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    const trim = (s) => (s || "").trim();
    const nombre = trim(form.nombre);
    const rut = trim(form.rut);
    const telefono = trim(form.telefono);
    const correo = trim(form.correo);
    const monto = trim(form.monto);

    if (!nombre || !rut || !telefono || !correo || !monto) {
      setErrorMsg("Por favor completa todos los campos.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(correo)) {
      setErrorMsg("Ingresa un correo válido.");
      return;
    }

    setSubmitting(true);

    // Fire the Google Ads conversion event WITHOUT depending on its callback
    // (callback can delay or fail silently and block the redirect).
    try {
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-18052207540/BIJzCLuXw74cELSn-59D",
          value: 1.0,
          currency: "COP",
        });
      }
    } catch (_) {
      /* ignore */
    }

    // Fallback URL in case the backend is unreachable.
    let waUrl = nextWhatsAppUrl();

    // POST the lead. The backend assigns the next redirect_url from the
    // configured rotation and returns it in the response. Race against a
    // 1.2s cap so navigation never stalls — fetch uses keepalive so the
    // request survives the navigation if it hasn't resolved yet.
    const payload = JSON.stringify({
      nombre,
      rut,
      telefono,
      correo,
      monto,
      origen,
    });

    try {
      const result = await Promise.race([
        fetch(`${API}/leads`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
          keepalive: true,
        })
          .then((r) => r.json())
          .catch(() => null),
        new Promise((r) => setTimeout(() => r(null), 1200)),
      ]);
      if (result && result.redirect_url) {
        waUrl = result.redirect_url;
      }
    } catch (_) {
      /* keepalive request will continue in the background */
    }

    // Redirect in the SAME window. No popup, no delay.
    window.location.href = waUrl;
  };

  return (
    <LeadDialogContext.Provider value={{ open }}>
      {children}
      <Dialog
        open={isOpen}
        onOpenChange={(v) => {
          if (submitting) return;
          setIsOpen(v);
          if (!v) reset();
        }}
      >
        <DialogContent
          className="sm:max-w-md p-0 overflow-hidden gap-0"
          data-testid="lead-dialog"
        >
          <div className="px-6 pt-6 pb-4 border-b border-slate-100">
            <DialogHeader className="space-y-1.5">
              <DialogTitle className="text-xl font-bold tracking-tight text-slate-900">
                Solicita tu evaluación
              </DialogTitle>
              <DialogDescription className="text-sm text-slate-500">
                Completa estos datos y te conectaremos con un asesor por
                WhatsApp.
              </DialogDescription>
            </DialogHeader>
          </div>

          <form
            onSubmit={submit}
            className="px-6 py-5 space-y-4"
            data-testid="lead-form"
            noValidate
          >
                <div className="space-y-1.5">
                  <Label htmlFor="lead-nombre" className="text-xs font-semibold text-slate-700">
                    Nombre completo
                  </Label>
                  <Input
                    id="lead-nombre"
                    value={form.nombre}
                    onChange={change("nombre")}
                    placeholder="Ej. María González"
                    autoComplete="name"
                    data-testid="lead-nombre"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="lead-rut" className="text-xs font-semibold text-slate-700">
                      RUT
                    </Label>
                    <Input
                      id="lead-rut"
                      value={form.rut}
                      onChange={change("rut")}
                      placeholder="12.345.678-9"
                      data-testid="lead-rut"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lead-telefono" className="text-xs font-semibold text-slate-700">
                      Teléfono
                    </Label>
                    <Input
                      id="lead-telefono"
                      value={form.telefono}
                      onChange={change("telefono")}
                      placeholder="+56 9 1234 5678"
                      inputMode="tel"
                      autoComplete="tel"
                      data-testid="lead-telefono"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="lead-correo" className="text-xs font-semibold text-slate-700">
                    Correo electrónico
                  </Label>
                  <Input
                    id="lead-correo"
                    type="email"
                    value={form.correo}
                    onChange={change("correo")}
                    placeholder="tu@email.cl"
                    autoComplete="email"
                    data-testid="lead-correo"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="lead-monto" className="text-xs font-semibold text-slate-700">
                    Monto a solicitar (CLP)
                  </Label>
                  <Input
                    id="lead-monto"
                    value={form.monto}
                    onChange={change("monto")}
                    placeholder="Ej. 1.500.000"
                    inputMode="numeric"
                    data-testid="lead-monto"
                  />
                </div>

                <p className="text-[11px] text-slate-500 leading-snug pt-1">
                  Al enviar aceptas que tus datos sean tratados para gestionar
                  tu solicitud. Bancoorp actúa como intermediario y no otorga
                  créditos directamente.
                </p>

                {errorMsg && (
                  <div
                    className="text-xs rounded-md bg-red-50 border border-red-200 text-red-700 px-3 py-2"
                    data-testid="lead-error"
                  >
                    {errorMsg}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full py-5 text-sm font-semibold"
                  data-testid="lead-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Enviando…
                    </>
                  ) : (
                    <>
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Enviar y continuar por WhatsApp
                    </>
                  )}
                </Button>
              </form>
        </DialogContent>
      </Dialog>
    </LeadDialogContext.Provider>
  );
}
